import { apiClient } from '../../../services/api/apiClient.js';
import { ApiError, type ApiResponse } from '../../../services/api/apiClient.types.js';

export interface LoyaltyModuleHealth {
  readonly module: string;
  readonly status: string;
  readonly moduleEnabled: boolean;
  readonly tenantEnabled: boolean;
  readonly disabledScope?: 'global' | 'tenant';
}

export interface LoyaltyProgramSettings {
  readonly moduleEnabled: boolean;
  readonly tenantEnabled: boolean;
  readonly status: 'active' | 'disabled' | 'unknown';
}

export interface LoyaltyCampaignSummary {
  readonly campaignId: string;
  readonly name: string;
  readonly status: 'active' | 'draft' | 'ended' | 'paused';
  readonly startsAt?: string;
  readonly endsAt?: string;
}

export interface LoyaltyTenantConfigDTO {
  readonly moduleEnabled: boolean;
  readonly earnPointsPerTenCzk: number;
  readonly registrationBonusPoints: number;
  readonly firstTransactionBonusPoints: number;
  readonly walletLazyCreateEnabled: boolean;
}

export interface PhysicalCardRow {
  readonly id: string;
  readonly cardCode: string;
  readonly status: 'active' | 'revoked';
  readonly customerId?: number;
  readonly displayHint?: string | null;
  readonly issuedAt: string;
}

export interface IssuePhysicalCardInput {
  readonly customerId?: number;
  readonly displayHint?: string;
}

export interface PaginatedCampaignsResponse {
  readonly items: readonly LoyaltyCampaignSummary[];
  readonly total: number;
  readonly page: number;
  readonly limit: number;
}

type LoyaltyHealthPayload = {
  readonly module?: string;
  readonly status?: string;
  readonly moduleEnabled?: boolean;
  readonly tenantEnabled?: boolean;
  readonly disabledScope?: 'global' | 'tenant';
};

type LoyaltyHealthResponse = ApiResponse<LoyaltyHealthPayload> & LoyaltyHealthPayload;

type LoyaltyDisabledDetails = {
  readonly scope?: 'global' | 'tenant';
};

function mapHealthPayload(payload: LoyaltyHealthPayload): LoyaltyModuleHealth {
  const moduleEnabled = payload.moduleEnabled ?? payload.status === 'ok';
  const tenantEnabled = payload.tenantEnabled ?? payload.status === 'ok';

  return {
    module: payload.module ?? 'loyalty',
    status: payload.status ?? (moduleEnabled && tenantEnabled ? 'ok' : 'disabled'),
    moduleEnabled,
    tenantEnabled,
    ...(payload.disabledScope ? { disabledScope: payload.disabledScope } : {}),
  };
}

function mapDisabledHealthFromApiError(error: ApiError): LoyaltyModuleHealth {
  const response = error.response as {
    details?: LoyaltyDisabledDetails;
  } | null;
  const scope = response?.details?.scope ?? 'global';

  if (scope === 'tenant') {
    return {
      module: 'loyalty',
      status: 'disabled',
      moduleEnabled: true,
      tenantEnabled: false,
      disabledScope: 'tenant',
    };
  }

  return {
    module: 'loyalty',
    status: 'disabled',
    moduleEnabled: false,
    tenantEnabled: false,
    disabledScope: 'global',
  };
}

export async function fetchLoyaltyModuleHealth(): Promise<LoyaltyModuleHealth> {
  try {
    const response = (await apiClient.get<LoyaltyHealthPayload>(
      '/api/v1/loyalty/health',
      false,
    )) as LoyaltyHealthResponse;

    if (!response.success) {
      throw new Error('Could not load loyalty module health');
    }

    const payload: LoyaltyHealthPayload = {
      module: response.data?.module ?? response.module,
      status: response.data?.status ?? response.status,
      moduleEnabled: response.data?.moduleEnabled ?? response.moduleEnabled,
      tenantEnabled: response.data?.tenantEnabled ?? response.tenantEnabled,
      disabledScope: response.data?.disabledScope ?? response.disabledScope,
    };

    return mapHealthPayload(payload);
  } catch (error) {
    if (error instanceof ApiError && error.statusCode === 403 && error.code === 'LOYALTY_DISABLED') {
      return mapDisabledHealthFromApiError(error);
    }
    throw error;
  }
}

/** AuthZ twin — no CERT_SCOPE CTA */
export async function enrollLoyaltyCampaign(campaignId: string): Promise<{ enrolled: boolean }> {
  const response = await apiClient.post(
    `/api/v1/admin/loyalty/campaigns/${encodeURIComponent(campaignId)}/enroll`,
    {},
    false,
  );

  if (!response.success) {
    throw new Error('Could not enroll loyalty campaign');
  }

  return { enrolled: true };
}

type LoyaltyTenantConfigEnvelope = {
  readonly config: LoyaltyTenantConfigDTO;
};

function unwrapTenantConfig(response: ApiResponse<LoyaltyTenantConfigEnvelope>): LoyaltyTenantConfigDTO {
  const config = response.data?.config;
  if (!response.success || config === undefined) {
    throw new Error('Could not load loyalty business partner config');
  }
  return config;
}

export async function fetchLoyaltyTenantConfig(): Promise<LoyaltyTenantConfigDTO> {
  try {
    const response = await apiClient.get<LoyaltyTenantConfigEnvelope>(
      '/api/v1/admin/loyalty/tenant-config',
    );
    return unwrapTenantConfig(response);
  } catch (error) {
    if (error instanceof ApiError && error.statusCode === 403 && error.code === 'LOYALTY_DISABLED') {
      throw error;
    }
    throw error;
  }
}

export async function updateLoyaltyTenantConfig(
  input: Omit<LoyaltyTenantConfigDTO, 'moduleEnabled'> & { moduleEnabled?: boolean },
): Promise<LoyaltyTenantConfigDTO> {
  const body = {
    earnPointsPerTenCzk: input.earnPointsPerTenCzk,
    registrationBonusPoints: input.registrationBonusPoints,
    firstTransactionBonusPoints: input.firstTransactionBonusPoints,
    walletLazyCreateEnabled: input.walletLazyCreateEnabled,
  };
  const response = await apiClient.put<LoyaltyTenantConfigEnvelope>(
    '/api/v1/admin/loyalty/tenant-config',
    body,
  );
  return unwrapTenantConfig(response);
}

export async function fetchLoyaltyCampaignsPage(
  page = 1,
  limit = 20,
): Promise<PaginatedCampaignsResponse> {
  const response = await apiClient.get<PaginatedCampaignsResponse>(
    `/api/v1/admin/loyalty/campaigns?page=${encodeURIComponent(String(page))}&limit=${encodeURIComponent(String(limit))}`,
  );
  if (!response.success || response.data === undefined) {
    throw new Error('Could not load loyalty campaigns');
  }
  return response.data;
}

export async function fetchPhysicalCards(): Promise<readonly PhysicalCardRow[]> {
  const response = await apiClient.get<{ items: PhysicalCardRow[] }>(
    '/api/v1/admin/loyalty/physical-cards',
  );
  if (!response.success || response.data === undefined) {
    throw new Error('Could not load physical cards');
  }
  return response.data.items ?? [];
}

export async function issuePhysicalCard(
  input: IssuePhysicalCardInput,
): Promise<PhysicalCardRow> {
  const response = await apiClient.post<PhysicalCardRow>(
    '/api/v1/admin/loyalty/physical-cards',
    input,
  );
  if (!response.success || response.data === undefined) {
    throw new Error('Could not issue physical card');
  }
  return response.data;
}

export async function revokePhysicalCard(cardId: string): Promise<PhysicalCardRow> {
  const response = await apiClient.post<PhysicalCardRow>(
    `/api/v1/admin/loyalty/physical-cards/${encodeURIComponent(cardId)}/revoke`,
    {},
  );
  if (!response.success || response.data === undefined) {
    throw new Error('Could not revoke physical card');
  }
  return response.data;
}

export async function issueLoyaltyCoupon(input: {
  readonly customerId: number;
  readonly discountAmount?: number;
  readonly discountBps?: number;
  readonly campaignId?: string | null;
  readonly expiresAt?: string | null;
}): Promise<{ readonly couponId: string; readonly code: string }> {
  const response = await apiClient.post<{ coupon: { couponId: string; code: string } }>(
    '/api/v1/admin/loyalty/coupons/issue',
    input,
  );
  if (!response.success) {
    throw new Error('Could not issue loyalty coupon');
  }
  const envelope = response as ApiResponse<{ coupon: { couponId: string; code: string } }>;
  const coupon = envelope.data?.coupon;
  if (coupon?.couponId === undefined || coupon.code === undefined) {
    throw new Error('Could not issue loyalty coupon');
  }
  return { couponId: coupon.couponId, code: coupon.code };
}

export function physicalCardArtifactUrl(cardId: string, kind: 'linear' | 'qr'): string {
  return `/api/v1/admin/loyalty/physical-cards/${encodeURIComponent(cardId)}/artifacts/${kind}`;
}
